import type { AgentAccent, GroupRef } from '@ralph/contracts'
import clsx from 'clsx'
import { groupIconComponent } from '@/components/groups/group-icons.ts'
import { accentTileStyle } from '@/components/marketplace/accents.ts'

/**
 * A group's avatar (PRD-26 D-6): a circle filled with its colour and its icon in white.
 *
 * A circle, where an agent's tile is a rounded square — the two sit side by side in pickers and
 * lists, and the shape is what tells a person from a thing at a glance. The fill comes from
 * `accentTileStyle`, the same six tokens agents use, so a group never paints outside the palette
 * and the glyph keeps its contrast in both themes (the ratio is accent-vs-white, not
 * accent-vs-page).
 *
 * Decorative by default: a group always renders with its name beside it (GRP-102 — never colour
 * alone, never an id), and announcing "Finance, Finance" helps nobody. Pass `label` only where
 * the avatar stands alone.
 */

export type GroupAvatarSize = 16 | 20 | 28 | 44

const GLYPH: Record<GroupAvatarSize, { icon: number; stroke: number }> = {
  16: { icon: 9, stroke: 2.4 },
  20: { icon: 11, stroke: 2.3 },
  28: { icon: 14, stroke: 2.1 },
  44: { icon: 21, stroke: 1.9 }
}

export const GroupAvatar = ({
  group,
  size = 28,
  label,
  className
}: {
  group: Pick<GroupRef, 'icon' | 'color'>
  size?: GroupAvatarSize
  /** Only where the avatar is not next to the group's name. */
  label?: string
  className?: string
}) => {
  const Icon = groupIconComponent(group.icon)
  const glyph = GLYPH[size]
  return (
    <span
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
      className={clsx('inline-flex shrink-0 items-center justify-center rounded-full', className)}
      style={{ ...accentTileStyle(group.color as AgentAccent), width: size, height: size }}
    >
      <Icon size={glyph.icon} strokeWidth={glyph.stroke} aria-hidden />
    </span>
  )
}
